import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Users, Briefcase } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MOCK_TEAM, capacityLevel, type TeamMember } from "./mockData";

// Extended palette — 12 visually distinct hues so common department combinations
// (e.g. Engineering + People & Culture) never share the same colour.
const DEPT_PALETTE: { bg: string; border: string; text: string; dot: string; hex: string }[] = [
  { bg: "bg-indigo-500/8",   border: "border-indigo-500/25",   text: "text-indigo-500",   dot: "bg-indigo-500",   hex: "#6366f1" },
  { bg: "bg-rose-500/8",     border: "border-rose-500/25",     text: "text-rose-500",     dot: "bg-rose-500",     hex: "#f43f5e" },
  { bg: "bg-emerald-500/8",  border: "border-emerald-500/25",  text: "text-emerald-500",  dot: "bg-emerald-500",  hex: "#10b981" },
  { bg: "bg-amber-500/8",    border: "border-amber-500/25",    text: "text-amber-500",    dot: "bg-amber-500",    hex: "#f59e0b" },
  { bg: "bg-sky-500/8",      border: "border-sky-500/25",      text: "text-sky-500",      dot: "bg-sky-500",      hex: "#0ea5e9" },
  { bg: "bg-pink-500/8",     border: "border-pink-500/25",     text: "text-pink-500",     dot: "bg-pink-500",     hex: "#ec4899" },
  { bg: "bg-teal-500/8",     border: "border-teal-500/25",     text: "text-teal-500",     dot: "bg-teal-500",     hex: "#14b8a6" },
  { bg: "bg-orange-500/8",   border: "border-orange-500/25",   text: "text-orange-500",   dot: "bg-orange-500",   hex: "#f97316" },
  { bg: "bg-violet-500/8",   border: "border-violet-500/25",   text: "text-violet-500",   dot: "bg-violet-500",   hex: "#8b5cf6" },
  { bg: "bg-cyan-500/8",     border: "border-cyan-500/25",     text: "text-cyan-500",     dot: "bg-cyan-500",     hex: "#06b6d4" },
  { bg: "bg-lime-500/8",     border: "border-lime-500/25",     text: "text-lime-500",     dot: "bg-lime-500",     hex: "#84cc16" },
  { bg: "bg-fuchsia-500/8",  border: "border-fuchsia-500/25",  text: "text-fuchsia-500",  dot: "bg-fuchsia-500",  hex: "#d946ef" },
];

// Stable assignment: hash department name → palette index so the same
// department always gets the same colour, even for unknown departments.
function hashDept(dept: string): number {
  let h = 0;
  for (let i = 0; i < dept.length; i++) h = (h * 31 + dept.charCodeAt(i)) >>> 0;
  return h % DEPT_PALETTE.length;
}

const LANE_COLORS: Record<string, typeof DEPT_PALETTE[number]> = {};

function getLaneColors(dept: string) {
  if (!LANE_COLORS[dept]) LANE_COLORS[dept] = DEPT_PALETTE[hashDept(dept)];
  return LANE_COLORS[dept];
}

// ── Employee card ─────────────────────────────────────────────────────────

interface MemberCardProps {
  member: TeamMember;
}

function MemberCard({ member }: MemberCardProps) {
  const { t } = useTranslation();
  const colors = getLaneColors(member.department);

  const statusLabel =
    member.status === "active"
      ? t("common.active")
      : member.status === "on_leave"
        ? t("common.onLeave")
        : t("teamOverview.remote");

  const statusDotClass =
    member.status === "active"
      ? "bg-emerald-500"
      : member.status === "on_leave"
        ? "bg-amber-500"
        : "bg-sky-400";

  const capLvl = capacityLevel(member.capacityPercent);
  const capBarClass =
    capLvl === "over"
      ? "bg-rose-500"
      : capLvl === "busy"
        ? "bg-amber-400"
        : "bg-emerald-500";
  const capLabelClass =
    capLvl === "over"
      ? "text-rose-500"
      : capLvl === "busy"
        ? "text-amber-500"
        : "text-emerald-500";

  return (
    <div className="flex flex-col rounded-xl border border-border/60 bg-card w-[160px] shrink-0 hover:border-primary/30 hover:shadow-md transition-all duration-200 overflow-hidden group">
      {/* Department colour accent strip */}
      <div className="h-1 w-full shrink-0" style={{ background: colors.hex }} />

      <div className="flex flex-col items-center gap-2.5 px-3 pt-4 pb-3">
        {/* Avatar with status indicator — colour derived from department */}
        <div className="relative">
          <div
            className="h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm ring-2 ring-background"
            style={{ background: colors.hex }}
          >
            {member.initials || member.name?.slice(0, 2).toUpperCase() || "?"}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-card",
                  statusDotClass,
                )}
              />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {statusLabel}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Name + role */}
        <div className="text-center min-w-0 w-full space-y-0.5">
          <p className="text-xs font-semibold leading-tight truncate">
            {member.name}
          </p>
          <div className="flex items-center justify-center gap-1 min-w-0">
            <Briefcase className="h-2.5 w-2.5 shrink-0 text-muted-foreground/60" />
            <p className="text-[10px] text-muted-foreground leading-tight truncate">
              {member.role}
            </p>
          </div>
          {member.isNewHire && (
            <Badge
              variant="secondary"
              className="text-[9px] px-1.5 py-0 h-4 leading-none mt-1"
            >
              {t("teamOverview.newBadge")}
            </Badge>
          )}
        </div>

        {/* Capacity bar */}
        <div className="w-full pt-2 border-t border-border/40">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground font-medium">
              {t("teamOverview.capacityLabel")}
            </span>
            <span className={cn("text-[10px] font-bold tabular-nums", capLabelClass)}>
              {member.capacityPercent}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={cn("h-full rounded-full transition-all duration-500", capBarClass)}
              style={{ width: `${Math.min(member.capacityPercent, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────

interface SwimlanesWidgetProps {
  members?: TeamMember[];
  isHR?: boolean;
}

export function SwimlanesWidget({ members: membersProp, isHR = false }: SwimlanesWidgetProps) {
  const { t } = useTranslation();
  const members = useMemo(() => membersProp ?? [], [membersProp]);

  // Unique departments (stable order of first appearance)
  const departments = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const m of members) {
      if (m.department && !seen.has(m.department)) {
        seen.add(m.department);
        order.push(m.department);
      }
    }
    return order.sort((a, b) => a.localeCompare(b));
  }, [members]);

  // Cards sorted: by department alphabetically, then name within dept
  const sorted = useMemo(
    () =>
      [...members].sort((a, b) => {
        const dc = (a.department ?? "").localeCompare(b.department ?? "");
        return dc !== 0 ? dc : a.name.localeCompare(b.name);
      }),
    [members],
  );

  const totalMembers = members.length;
  const onLeave = members.filter((m) => m.status === "on_leave").length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-indigo-500/10">
              <Users className="h-4 w-4 text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold">
                {t("teamOverview.swimlanesTitle")}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isHR
                  ? t("teamOverview.swimlanesSubtitleHR")
                  : t("teamOverview.swimlanesSubtitle")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
              {totalMembers} {t("teamOverview.totalHeadcount").toLowerCase()}
            </Badge>
            {onLeave > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 border-amber-500/40 text-amber-400 bg-amber-500/10"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {onLeave} {t("common.onLeave").toLowerCase()}
              </Badge>
            )}
          </div>
        </div>

        {/* Department colour legend */}
        {departments.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-2 mt-1 border-t border-border/40">
            {departments.map((dept) => {
              const c = getLaneColors(dept);
              const count = members.filter((m) => m.department === dept).length;
              return (
                <div key={dept} className="flex items-center gap-1.5">
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ background: c.hex }}
                  />
                  <span className="text-[11px] text-muted-foreground">
                    {dept}
                    <span className="ml-1 text-[10px] opacity-60">({count})</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0 pb-4">
        {sorted.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">
            {t("teamOverview.noMatchingEmployees")}
          </p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1"
               style={{ scrollbarWidth: "thin" }}>
            {sorted.map((m) => (
              <MemberCard key={m.id} member={m} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
